/** Menu geometry is measured in pixels; CSS cannot interpolate width:auto. */
export function naturalMenuWidth(element: HTMLElement): string {
  const width = element.style.width;
  const priority = element.style.getPropertyPriority('width');
  // Important temporarily wins over a still-running width animation during a reversal.
  element.style.setProperty('width', 'auto', 'important');
  const measured = element.getBoundingClientRect().width;
  if (width) element.style.setProperty('width', width, priority);
  else element.style.removeProperty('width');
  return measured + 'px';
}

export class MenuMotion {
  private animation?: Animation;
  private revision = 0;

  play(
    element: HTMLElement,
    target: Partial<CSSStyleDeclaration>,
    duration: number,
    fixedAfter = false,
  ): void {
    const current = getComputedStyle(element);
    const from: Keyframe = {};
    for (const property of Object.keys(target)) {
      const key = property as keyof CSSStyleDeclaration;
      const value = current[key];
      if (typeof value === 'string') from[property] = value;
    }
    const revision = ++this.revision;
    this.animation?.cancel();
    this.animation = undefined;
    Object.assign(element.style, target);
    const complete = () => {
      if (revision !== this.revision) return;
      if (fixedAfter) element.style.position = 'fixed';
      this.animation?.cancel();
      this.animation = undefined;
    };
    const reduced = element.ownerDocument.defaultView?.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (reduced || !duration || typeof element.animate !== 'function') {
      complete();
      return;
    }
    const to: Keyframe = {};
    for (const [key, value] of Object.entries(target))
      if (typeof value === 'string') to[key] = value;
    try {
      // Closing the pinned submenu shrinks for 80% of the interval, then fixes its position.
      const frames = fixedAfter
        ? [from, { ...to, offset: 0.8 }, { ...to, offset: 1 }]
        : [from, to];
      this.animation = element.animate(frames, { duration, easing: 'linear' });
      this.animation.finished.then(complete, complete);
    } catch {
      complete();
    }
  }

  destroy(): void {
    ++this.revision;
    this.animation?.cancel();
    this.animation = undefined;
  }
}
